// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@tls/auth';
import type { ReleaseChangeLogContent } from 'model/ReleaseInformation';
import { useCallback, useEffect, useState } from 'react';
import { getCurrentEmaiVersion, getEmaiReleaseLine, getEmaiVersionStorageKey } from 'utilities/emaiVersion';
import { isUiReloadPending, type UiVersionStatus } from 'utilities/uiVersion';
import useReleaseInformationApi, { ReleaseInformationError, releaseQuerySettings } from './useReleaseInformationApi';

export default function useReleaseChangeLog(uiVersionStatus: UiVersionStatus | undefined, manualOpen: boolean) {
  const { appBaseUrl = '' } = useAuthContext();
  const [automaticApp, setAutomaticApp] = useState<string | null>(null);
  const automatic = automaticApp === appBaseUrl;
  const enabled = uiVersionStatus === 'available' && !isUiReloadPending();
  const versionQuery = useQuery({
    ...releaseQuerySettings,
    queryKey: ['emai-version', appBaseUrl],
    queryFn: getCurrentEmaiVersion,
    enabled,
  });
  const notesQuery = useReleaseInformationApi(appBaseUrl, enabled);

  const { refetch: fetchVersion } = versionQuery;
  const { refetch: fetchNotes } = notesQuery;
  const refresh = useCallback(
    () =>
      Promise.allSettled([
        fetchVersion({ throwOnError: true, cancelRefetch: false }),
        fetchNotes({ throwOnError: true, cancelRefetch: false }),
      ]),
    [fetchVersion, fetchNotes]
  );

  useEffect(() => {
    if (!manualOpen) return;
    setAutomaticApp(null);
    void refresh();
  }, [manualOpen, refresh]);

  const releaseLine =
    versionQuery.isSuccess && notesQuery.isSuccess && !versionQuery.isFetching && !notesQuery.isFetching
      ? getEmaiReleaseLine(versionQuery.data)
      : null;

  useEffect(() => {
    if (!enabled || manualOpen || !releaseLine) return;
    try {
      const storageKey = getEmaiVersionStorageKey(appBaseUrl);
      const previousReleaseLine = localStorage.getItem(storageKey);
      if (!previousReleaseLine) {
        localStorage.setItem(storageKey, releaseLine);
      } else if (previousReleaseLine !== releaseLine) {
        setAutomaticApp(appBaseUrl);
      }
    } catch (error) {
      console.warn('Unable to access the saved EMAI version', error);
    }
  }, [enabled, manualOpen, appBaseUrl, releaseLine]);

  let content: ReleaseChangeLogContent;
  if (notesQuery.data) {
    content = { status: 'ready', releases: notesQuery.data.releases };
  } else if (notesQuery.isFetching || notesQuery.isPending) {
    content = { status: 'loading' };
  } else {
    content = {
      status: 'error',
      reason: notesQuery.error instanceof ReleaseInformationError ? notesQuery.error.reason : 'loadError',
    };
  }

  return {
    automatic,
    close: () => {
      if (versionQuery.data && content.status === 'ready' && !isUiReloadPending()) {
        try {
          localStorage.setItem(getEmaiVersionStorageKey(appBaseUrl), getEmaiReleaseLine(versionQuery.data));
        } catch (error) {
          console.warn('Unable to access the saved EMAI version', error);
        }
      }
      setAutomaticApp(null);
    },
    retry: () => void refresh(),
    version: versionQuery.data,
    content,
  };
}
